import { FC, useEffect, useState } from "react";
import api from "../../utils/api";
import { USER_PASSWORD_ANONYMUS } from "../../utils/endpoints";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Toast } from "../../components/atom/Toast/Toast";
import {
  Box,
  Heading,
  Stack,
  FormControl,
  Input,
  Button,
  Flex,
} from "@chakra-ui/react";
import LabelComponent from "../../components/atom/Label/LabelComponent";
import PasswordStrengthBarComponent from "../../components/organism/PasswordStrengthBar/PasswordStrengthBarComponent";
import { useLocation } from "react-router-dom";
import jwtDecode from "jwt-decode";
import PasswordDefinitionSuccessful from "./PasswordDefinitionSuccessful";
import InputFieldComponent from "../../components/molecule/InputField/InputFieldComponent";
import { PassowrdItemSchema } from "./data/LoginItemSchema";
import { errorColor, successColor } from "../../utils/colors";
import { AuthUser } from "../../types/User/AuthUser";
interface FormData {
  newPassword: string;
  confirmPassword: string;
}

const PasswordDefinition: FC = () => {
  const { addToast } = Toast();
  const { search } = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const rules: RegExp[] = [
    /[0-9]/,
    /^(?=.*[a-z])(?=.*[A-Z])/,
    /[^A-Za-z0-9]/,
    /.{12,}/,
  ];

  const descriptions = [
    "A senha deve conter números;",
    "A senha tem de conter letras maiúsculas e minúsculas;",
    "A senha deve conter pelo menos um símbolo;",
    "A senha tem de ter mais de 12 caracteres;",
  ];

  const barColors = [errorColor, "yellow.500", successColor];

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(PassowrdItemSchema),
  });

  let newPasswordValue = watch("newPassword");
  newPasswordValue = newPasswordValue === undefined ? "" : newPasswordValue;

  useEffect(() => {
    const token: string | undefined =
      search.length > 1 ? search.slice(1) : undefined;
    if (token) {
      setToken(token);
      const user: AuthUser = jwtDecode(token);
      if (user.email) {
        setEmail(user.email);
      }
    }
  }, [search]);

  const onSubmit = handleSubmit((data, event) => {
    event?.preventDefault();
    api
      .patch(USER_PASSWORD_ANONYMUS, {
        tokenString: token,
        password: data.newPassword,
        passwordConfirmation: data.confirmPassword,
      })
      .then((response) => {
        if (response.data.statusCode === 200) {
          setIsSubmitted(true);
        } else {
          addToast({
            title: "Erro",
            description: response.data.message,
            status: "error",
          });
        }
      })
      .catch((error) => {
        console.log("API Error:", error);
        addToast({
          title: "Erro",
          description: "Erro ao criar a senha",
          status: "error",
        });
      });
  });

  if (isSubmitted) {
    return <PasswordDefinitionSuccessful />;
  }

  return (
    <Box
      as="form"
      onSubmit={onSubmit}
      width="100%"
      display="flex"
      flexDirection="column"
      gap={8}
    >
      <Stack spacing={4}>
        <Heading as="h1">Definição da senha</Heading>
        <FormControl>
          <LabelComponent title="Utilizador" fontWeight="500" fontSize="md" />
          <Input
            variant="secondary"
            type="email"
            value={email || ""}
            disabled
            fontWeight="400"
            fontSize="md"
          />
        </FormControl>
        <Flex gap={2} direction="column">
          <InputFieldComponent
            label="Nova Senha"
            name="newPassword"
            type="password"
            register={register}
            errors={errors}
            isRequired
            isPassword
          />
          <PasswordStrengthBarComponent
            password={newPasswordValue}
            passwordDescriptions={descriptions}
            passwordRules={rules}
            barColors={barColors}
          />
        </Flex>
        <InputFieldComponent
          label="Confirmar Senha"
          name="confirmPassword"
          type="password"
          register={register}
          errors={errors}
          isRequired
        />
      </Stack>
      <Button type="submit" alignSelf="flex-start">
        Definir
      </Button>
    </Box>
  );
};

export default PasswordDefinition;
